import apiClient from './api';
import {
  CostCenterDto,
  CreateCostCenterDto,
  UpdateCostCenterDto,
  CostCenterTreeDto
} from '../types/costCenters.types';

// Export types for external use
export type { CostCenterDto, CreateCostCenterDto, UpdateCostCenterDto, CostCenterTreeDto };

class CostCentersService {
  private baseUrl = '/CostCenters';

  // Get all cost centers
  async getAll(includeInactive?: boolean, onlyFinal?: boolean): Promise<CostCenterDto[]> {
    const params = new URLSearchParams();
    if (includeInactive !== undefined) {
      params.append('includeInactive', includeInactive.toString());
    }
    if (onlyFinal !== undefined) {
      params.append('onlyFinalCostCenters', onlyFinal.toString());
    }

    const url = params.toString() ? `${this.baseUrl}?${params.toString()}` : this.baseUrl;
    const response = await apiClient.get(url);
    return response.data;
  }

  // Get cost center by ID
  async getById(id: number): Promise<CostCenterDto> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // Get cost centers tree
  async getTree(includeInactive?: boolean): Promise<CostCenterTreeDto[]> {
    const params = new URLSearchParams();
    if (includeInactive !== undefined) {
      params.append('includeInactive', includeInactive.toString());
    }

    const url = params.toString() 
      ? `${this.baseUrl}/tree?${params.toString()}` 
      : `${this.baseUrl}/tree`;
    const response = await apiClient.get(url);
    return response.data;
  }

  // Get next available code (Hierarchical)
  async getNextCode(parentCode?: string): Promise<string> {
    try {
      const allCostCenters = await this.getAll(true, false);
      
      
      // Level 1 (No Parent): CC-001, CC-002, CC-003, ..., CC-999
      if (!parentCode) {
        const level1CostCenters = allCostCenters.filter(cc => !cc.parentCostCenterId);
        
        
        if (level1CostCenters.length === 0) {
          return 'CC-001';
        }

        // Extract numbers from codes like CC-001, CC-002
        const level1Numbers = level1CostCenters
          .map(cc => {
            const code = cc.costCenterCode.trim();
            const match = code.match(/^CC-(\d+)$/);
            if (match) {
              return parseInt(match[1], 10);
            }
            return 0;
          })
          .filter(num => num > 0);


        if (level1Numbers.length === 0) {
          return 'CC-001';
        }

        const maxNumber = Math.max(...level1Numbers);
        const nextNumber = maxNumber + 1;


        if (nextNumber > 999) {
          throw new Error('Maximum level 1 cost centers reached (999)');
        }

        return `CC-${nextNumber.toString().padStart(3, '0')}`;
      }

      // Level 2+ (Has Parent): ParentCode-0001, ParentCode-0002, etc.
      
      const childrenCostCenters = allCostCenters.filter(cc => {
        if (!cc.parentCostCenterId) return false;
        
        // Find the parent to match the code
        const parent = allCostCenters.find(p => p.costCenterId === cc.parentCostCenterId);
        return parent && parent.costCenterCode === parentCode;
      });


      if (childrenCostCenters.length === 0) {
        // First child - always use 4 digits
        return `${parentCode}-0001`;
      }

      // Extract numbers from children codes
      const childrenNumbers = childrenCostCenters
        .map(cc => {
          // Extract the last segment number
          // Example: CC-001-0001 → extract 0001
          // Example: CC-001-0001-0002 → extract 0002
          const parts = cc.costCenterCode.split('-');
          const lastPart = parts[parts.length - 1];
          return parseInt(lastPart, 10) || 0;
        })
        .filter(num => num > 0);


      if (childrenNumbers.length === 0) {
        return `${parentCode}-0001`;
      }

      const maxNumber = Math.max(...childrenNumbers);
      const nextNumber = maxNumber + 1;

      // Always use 4 digits for children
      const result = `${parentCode}-${nextNumber.toString().padStart(4, '0')}`;

      return result;
    } catch (error) {
      console.error('Failed to get next code:', error);
      if (!parentCode) {
        return 'CC-001';
      }
      return `${parentCode}-0001`;
    }
  }

  // Create cost center
  async create(data: CreateCostCenterDto): Promise<CostCenterDto> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  // Update cost center
  async update(id: number, data: UpdateCostCenterDto): Promise<CostCenterDto> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  // Delete cost center
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }
}

export const costCentersService = new CostCentersService();